import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Layout } from '../Layout';

const adminLinks = [
    { href: '/manage-projects',     label: 'Projects' },
    { href: '/manage-blogs',        label: 'Blog posts' },
    { href: '/manage-technologies', label: 'Technologies' },
    { href: '/manage-users',        label: 'Users' },
];

interface Props {
    title: string;
    children: React.ReactNode;
}

export const AdminLayout: React.FC<Props> = ({ title, children }) => {
    const location = useLocation();

    return (
        <Layout>
            <div className="flex min-h-screen bg-background">
                {/* Sidebar */}
                <aside className="w-52 shrink-0 bg-surface border-r border-border flex flex-col p-4">
                    <p className="label mb-3 px-3">
                        Admin
                    </p>
                    <nav className="space-y-1">
                        {adminLinks.map(link => {
                            const active = location.pathname === link.href;
                            return (
                                <Link
                                    key={link.href}
                                    to={link.href}
                                    aria-current={active ? 'page' : undefined}
                                    className={`block px-3 py-2 rounded-sm font-label text-base transition-colors ${
                                        active
                                            ? 'bg-paper text-ink font-semibold'
                                            : 'text-muted hover:text-ink hover:bg-paper'
                                    }`}
                                >
                                    {link.label}
                                </Link>
                            );
                        })}
                    </nav>
                    <div className="mt-auto pt-4 border-t border-border">
                        <Link
                            to="/"
                            className="link font-label inline-flex items-center gap-1 px-3 py-2"
                        >
                            <ChevronLeft size={16} aria-hidden />
                            Back to site
                        </Link>
                    </div>
                </aside>

                {/* Main content */}
                <main className="flex-1 p-8 overflow-auto">
                    <h1 className="text-3xl mb-6">{title}</h1>
                    {children}
                </main>
            </div>
        </Layout>
    );
};

interface TabBarProps {
    tabs: { key: string; label: string }[];
    active: string;
    onChange: (key: string) => void;
}

export const TabBar: React.FC<TabBarProps> = ({ tabs, active, onChange }) => (
    <div className="flex gap-1 mb-6 border-b border-border">
        {tabs.map(tab => (
            <button
                key={tab.key}
                onClick={() => onChange(tab.key)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    active === tab.key
                        ? 'border-primary text-primary'
                        : 'border-transparent text-text-secondary hover:text-on-surface'
                }`}
            >
                {tab.label}
            </button>
        ))}
    </div>
);
